import React from 'react'
import ExpandableCard from '../ExpandableCard'
import SmartMarkdown from './SmartMarkdown'
import TrimesterGraphics from './TrimesterGraphics'
import { humanizedWeeksAndDays } from './helpers'
import states from './states.json'
import './AbortionDetails_styles.scss'
import pill_icon from './icon_pills_blue.svg'
import stethoscope_icon from './stethoscopecircle_blue.svg'
import { NavigateClinicOffering } from '../../../shared/constants.js'

const abortionPillCutOffGestationalAge = 91

const AbortionDetails = ({ clinic, stateAbortionLaw, lexicon }) => {
  const offersSurgical =
    clinic.known_offerings &&
    !!clinic.known_offerings[NavigateClinicOffering.SURGICAL_ABORTION]
  const offersMedical =
    clinic.known_offerings &&
    !!clinic.known_offerings[NavigateClinicOffering.MEDICATION_ABORTION_PILL]

  if (!offersSurgical && !offersMedical) return null

  const timeLexicon = lexicon.subset('time')

  let medicalSnippet = ''
  if (clinic.pill_gal_mode === 'FETAL_TONE')
    medicalSnippet = `${timeLexicon.get('through')} ${lexicon.get(
      'around_6_weeks'
    )}`
  else if (clinic.abortion_pill_gestational_age_max)
    medicalSnippet = `${timeLexicon.get('through')} ${humanizedWeeksAndDays(
      clinic.abortion_pill_gestational_age_max,
      timeLexicon
    )}`
  else medicalSnippet = lexicon.get('call_snippet')

  let surgicalSnippet = ''
  if (clinic.procedure_gal_mode === 'FETAL_TONE')
    surgicalSnippet = `${timeLexicon.get('through')} ${lexicon.get(
      'around_6_weeks'
    )}`
  else if (clinic.abortion_procedure_gestational_age_max)
    surgicalSnippet = `${timeLexicon.get('through')} ${humanizedWeeksAndDays(
      clinic.abortion_procedure_gestational_age_max,
      timeLexicon
    )}`
  else surgicalSnippet = lexicon.get('call_snippet')

  let bullet_1m = ''
  if (clinic.pill_gal_mode === 'FETAL_TONE')
    bullet_1m = lexicon.get('medical.known_both', {
      humanized_time: lexicon.get('around_6_weeks'),
    })
  else if (clinic.abortion_pill_gestational_age_max) {
    bullet_1m = lexicon.get('medical.known_both', {
      humanized_time: humanizedWeeksAndDays(
        clinic.abortion_pill_gestational_age_max,
        timeLexicon
      ),
    })
  } else bullet_1m = lexicon.get('medical.known_one')

  let bullet_1s = ''
  let bullet_2s = ''
  const stateGestationalCapInWeeks = Math.ceil(stateAbortionLaw.gal_days / 7)

  if (clinic.state && stateAbortionLaw) {
    if (stateAbortionLaw.gal_mode === 'NO_LIMIT')
      bullet_1s = lexicon.get('surgical.state_no_limit', {
        state: states[clinic.state],
      })
    else if (stateAbortionLaw.gal_mode === 'VIABILITY') {
      if (stateAbortionLaw.gestational_limit_exceptions.length)
        bullet_1s = lexicon.get('surgical.state_viability_exceptions', {
          state: states[clinic.state],
        })
      else
        bullet_1s = lexicon.get('surgical.state_viability_no_exceptions', {
          state: states[clinic.state],
        })
    } else if (stateAbortionLaw.gal_mode === 'FETAL_TONE') {
      // Fetal_Tone days in weeks
      bullet_1s = lexicon.get('surgical.state_ban_exceptions', {
        state: states[clinic.state],
        humanized_time: lexicon.get('around_6_weeks'),
      })
    } else if (stateAbortionLaw.gestational_limit_exceptions.length) {
      bullet_1s = lexicon.get('surgical.state_ban_exceptions', {
        state: states[clinic.state],
        humanized_time: humanizedWeeksAndDays(
          stateAbortionLaw.gal_days,
          timeLexicon
        ),
      })
    } else {
      bullet_1s = lexicon.get('surgical.state_ban_no_exceptions', {
        state: states[clinic.state],
        humanized_time: humanizedWeeksAndDays(
          stateAbortionLaw.gal_days,
          timeLexicon
        ),
      })
    }

    if (clinic.procedure_gal_mode === 'FETAL_TONE') {
      bullet_2s = lexicon.get('surgical.known_both', {
        humanized_time: lexicon.get('around_6_weeks'),
      })
    } else if (clinic.abortion_procedure_gestational_age_max) {
      bullet_2s = lexicon.get('surgical.known_both', {
        humanized_time: humanizedWeeksAndDays(
          clinic.abortion_procedure_gestational_age_max,
          timeLexicon
        ),
      })
    } else if (stateAbortionLaw.gal_mode === 'NO_LIMIT')
      bullet_2s = lexicon.get('surgical.known_one_no_limit')
    else if (stateAbortionLaw.gal_mode === 'VIABILITY')
      bullet_2s = lexicon.get('surgical.known_one_viability')
    else
      bullet_2s = lexicon.get('surgical.known_one_ban', {
        weeks: stateGestationalCapInWeeks,
      })
  }

  const medicalDetail = lexicon.get('medical.description', { bullet_1m })
  const surgicalDetail = lexicon.get('surgical.description', {
    bullet_1s,
    bullet_2s,
  })

  // Get the gestational age cap for each method;
  // MEDICAL: use the clinic-specific data if we have it,
  // or default to 12 weeks regardless of state laws
  const medicationAgeCap =
    clinic.abortion_pill_gestational_age_max || abortionPillCutOffGestationalAge

  // SURGICAL: use the clinic-specific data if we have it,
  // but fall back to state-law data if we don't.
  let surgicalAgeCap

  if (clinic.abortion_procedure_gestational_age_max)
    surgicalAgeCap = clinic.abortion_procedure_gestational_age_max
  else if (stateAbortionLaw.gal_mode === 'VIABILITY')
    surgicalAgeCap = 'viability'
  else surgicalAgeCap = stateAbortionLaw.gal_days

  return (
    <div id="AbortionDetails" className="abortion-section">
      <h3>{lexicon.get('title')}</h3>

      {offersMedical && (
        <ExpandableCard
          icon={pill_icon}
          title={lexicon.get('medical.title')}
          snippet={medicalSnippet}
        >
          {!Number.isNaN(medicationAgeCap) && (
            <TrimesterGraphics
              ageCap={medicationAgeCap}
              galMode={clinic.pill_gal_mode}
              medicalLimitKnown={!!clinic.abortion_pill_gestational_age_max}
              lexicon={lexicon.subset('TrimesterGraphics')}
              isMedical
            />
          )}
          <SmartMarkdown>{medicalDetail}</SmartMarkdown>
        </ExpandableCard>
      )}

      {offersSurgical && (
        <ExpandableCard
          icon={stethoscope_icon}
          title={lexicon.get('surgical.title')}
          snippet={surgicalSnippet}
        >
          {(!Number.isNaN(parseInt(surgicalAgeCap)) ||
            surgicalAgeCap === 'viability') && (
            <TrimesterGraphics
              ageCap={surgicalAgeCap}
              galMode={clinic.procedure_gal_mode}
              surgicalLimitKnown={
                !!clinic.abortion_procedure_gestational_age_max
              }
              clinicState={clinic.state}
              lexicon={lexicon.subset('TrimesterGraphics')}
              isSurgical
            />
          )}
          <SmartMarkdown>{surgicalDetail}</SmartMarkdown>
        </ExpandableCard>
      )}
    </div>
  )
}

export default AbortionDetails
